import { useMemo } from "react";
import ContentCard from "@/components/UI/Cards/ContentCard";
import StatCard from "@/components/UI/Cards/StatCard";
import PageHeader from "@/components/UI/PageHeader";
import breaches from "@/assets/data/json_output/breaches-issues.json";
import tasks from "@/assets/data/json_output/tasks.json";
import { TablePlot } from "@/views/TrialHomeScreen/components/TablePlots";
import { registerRisks, registerSignals } from "@/views/Risk/riskRegisterData";
import type { BreachTableRow } from "@/views/Risk/Breaches/utils";
import type { TaskTableRow } from "@/views/Risk/Tasks/utils";
import { buildRiskAnalytics } from "./utils";

const riskRows = registerRisks;
const signalRows = registerSignals;
const breachRows = breaches as BreachTableRow[];
const taskRows = tasks as TaskTableRow[];

export default function RiskAnalytics() {
  const analytics = useMemo(
    () => buildRiskAnalytics(riskRows, signalRows, breachRows, taskRows),
    [],
  );

  return (
    <div className="flex w-full min-w-0 flex-col gap-4 p-4">
      <PageHeader
        title="Analytics"
        tooltipText="Summarizes the connected risks, signals, issues, and remediation tasks in the RBQM dataset."
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <StatCard
          label="Total Risks"
          value={analytics.totalRisks}
          description="Current risk records represented in the portfolio"
        />
        <StatCard
          label="Signals In Breach"
          value={analytics.breachedSignals}
          description="Signals currently marked as having an active breach"
        />
        <StatCard
          label="Active Issues"
          value={analytics.activeIssues}
          description="Issue records that are not yet resolved or closed"
        />
        <StatCard
          label="Overdue Tasks"
          value={analytics.overdueTasks}
          description="Open remediation work with due dates earlier than today"
        />
        <StatCard
          label="Avg Risk Score"
          value={analytics.avgRiskScore}
          description="Mean assessed risk score across all tracked risks"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ContentCard
          title="Risk Portfolio By Category"
          subtitle="Shows where the portfolio is concentrated across major risk domains."
          contentClassName="min-h-[280px]"
        >
          <TablePlot
            data={[analytics.byRiskCategory]}
            mode="counts"
            preDefinedStacks={["Risks"]}
            stackLabels={{ Risks: "Risks" }}
            categoriesTitle="Category"
          />
        </ContentCard>
        <ContentCard
          title="Signal Health"
          subtitle="Highlights the current balance of green, yellow, and red signals."
          contentClassName="min-h-[280px]"
        >
          <TablePlot
            data={[analytics.bySignalHealth]}
            mode="counts"
            preDefinedStacks={["Signals"]}
            stackLabels={{ Signals: "Signals" }}
            categoriesTitle="Status"
            sorted={false}
          />
        </ContentCard>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ContentCard
          title="Issues By Status"
          subtitle="Surfaces how much signal-driven issue work remains open versus resolved."
          contentClassName="min-h-[280px]"
        >
          <TablePlot
            data={[analytics.byIssueStatus]}
            mode="counts"
            preDefinedStacks={["Issues"]}
            stackLabels={{ Issues: "Issues" }}
            categoriesTitle="Status"
          />
        </ContentCard>
        <ContentCard
          title="Task Priority Mix"
          subtitle="Shows how much remediation effort is concentrated in higher-priority work."
          contentClassName="min-h-[280px]"
        >
          <TablePlot
            data={[analytics.byTaskPriority]}
            mode="counts"
            preDefinedStacks={["Tasks"]}
            stackLabels={{ Tasks: "Tasks" }}
            categoriesTitle="Priority"
            sorted={false}
          />
        </ContentCard>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ContentCard
          title="Risk Score Bands"
          subtitle="Buckets assessed risk scores to quickly show where attention is concentrated."
          contentClassName="min-h-[280px]"
        >
          <TablePlot
            data={[analytics.byRiskScoreBand]}
            mode="counts"
            preDefinedStacks={["Risks"]}
            stackLabels={{ Risks: "Risks" }}
            categoriesTitle="Score"
            sorted={false}
          />
        </ContentCard>
        <ContentCard
          title="Escalation Levels"
          subtitle="Shows which governance paths are being used for current issue escalation."
          contentClassName="min-h-[280px]"
        >
          <TablePlot
            data={[analytics.byEscalationLevel]}
            mode="counts"
            preDefinedStacks={["Issues"]}
            stackLabels={{ Issues: "Issues" }}
            categoriesTitle="Escalation"
          />
        </ContentCard>
      </div>

      <ContentCard
        title="Ownership Load"
        subtitle="Compares portfolio ownership across risks, issues, and remediation tasks."
        contentClassName="min-h-[220px]"
      >
        <TablePlot
          data={[analytics.byOwnerLoad]}
          mode="counts"
          preDefinedStacks={["Risks", "Issues", "Tasks"]}
          stackLabels={{
            Risks: "Risks",
            Issues: "Issues",
            Tasks: "Tasks",
          }}
          categoriesTitle="Owner"
        />
      </ContentCard>
    </div>
  );
}
